import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import Card from '../common/Card';
import Button from '../common/Button';

export default function StudentList({ escolaId, refreshSignal }) {
    const [alunos, setAlunos] = useState([]);
    const [gates, setGates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if (escolaId) {
            loadInitialData();
        }
    }, [escolaId, refreshSignal]);

    const loadInitialData = async () => {
        try {
            setLoading(true);

            // 1. Carregar Portões
            const { data: gateData } = await supabase
                .from('portoes')
                .select('*')
                .eq('escola_id', escolaId)
                .order('nome');
            setGates(gateData || []);

            // 2. Carregar Alunos
            const { data, error } = await supabase
                .from('alunos')
                .select('*, portao:portoes(nome)')
                .eq('escola_id', escolaId)
                .order('nome_completo', { ascending: true });

            if (error) throw error;
            setAlunos(data || []);
        } catch (error) {
            console.error('Erro ao carregar alunos:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateStudentGate = async (alunoId, gateId) => {
        try {
            const { error } = await supabase
                .from('alunos')
                .update({ portao_id: gateId || null })
                .eq('id', alunoId);

            if (error) throw error;

            // Atualiza o estado local para ser mais rápido
            setAlunos(prev => prev.map(a =>
                a.id === alunoId ? { ...a, portao_id: gateId } : a
            ));
        } catch (error) {
            console.error('Erro ao atualizar portão do aluno:', error);
            alert('Erro ao atualizar portão.');
        }
    };

    const filteredAlunos = alunos.filter(aluno =>
        aluno.nome_completo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        aluno.turma?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading && alunos.length === 0) {
        return <div className="text-center py-12">Carregando alunos...</div>;
    }

    return (
        <div className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-4 justify-between items-center mb-6">
                <h2 className="text-xl font-bold">Alunos Cadastrados ({alunos.length})</h2>
                <div className="w-full sm:w-64">
                    <input
                        type="text"
                        placeholder="Pesquisar aluno ou turma..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                    />
                </div>
            </div>

            {filteredAlunos.length === 0 ? (
                <Card className="text-center py-12">
                    <div className="text-5xl mb-4">🔍</div>
                    <p className="text-gray-500">Nenhum aluno encontrado.</p>
                </Card>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredAlunos.map((aluno) => (
                        <Card key={aluno.id} className="hover:shadow-lg transition-all border border-transparent hover:border-primary/20">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xl font-bold">
                                    {aluno.nome_completo.charAt(0)}
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-bold text-gray-800 leading-tight">{aluno.nome_completo}</h3>
                                    <div className="flex flex-wrap items-center gap-2 mt-1">
                                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest px-1.5 py-0.5 bg-gray-50 rounded">
                                            {aluno.turma || 'Sem Turma'}
                                        </p>
                                        <select
                                            value={aluno.portao_id || ''}
                                            onChange={(e) => handleUpdateStudentGate(aluno.id, e.target.value)}
                                            className="text-[10px] text-primary font-black uppercase tracking-widest px-1.5 py-0.5 bg-primary/5 rounded border border-primary/10 outline-none cursor-pointer"
                                        >
                                            <option value="">🚪 S/ Portão</option>
                                            {gates.map(g => (
                                                <option key={g.id} value={g.id}>🚪 {g.nome}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <span className="block text-[10px] text-gray-400 font-bold uppercase">Código</span>
                                    <span className="text-sm font-mono font-bold text-primary bg-primary/5 px-2 py-1 rounded">
                                        {aluno.codigo_acesso || aluno.id.substring(0, 6).toUpperCase()}
                                    </span>
                                </div>
                            </div>

                            <div className="mt-4 pt-4 border-t flex justify-between items-center">
                                <button
                                    onClick={() => {
                                        const code = aluno.codigo_acesso || aluno.id.substring(0, 6).toUpperCase();
                                        // Pegar a URL atual do site para o QR Code funcionar em qualquer lugar (localhost ou produção)
                                        const baseUrl = window.location.origin;
                                        const linkCompleto = `${baseUrl}/cadastro?code=${code}`;

                                        const url = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(linkCompleto)}`;
                                        window.open(url, '_blank', 'width=400,height=450');
                                    }}
                                    className="text-xs flex items-center gap-1 text-gray-500 hover:text-primary transition-colors font-medium"
                                >
                                    <span>📱 Ver QR Code</span>
                                </button>
                                <div className="flex gap-2">
                                    <Button variant="outline" className="text-[11px] px-2 py-1 h-8">Editar</Button>
                                    <Button variant="outline" className="text-[11px] px-2 py-1 h-8 text-red-600 border-red-100">Excluir</Button>
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}
